import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/components/utils/utils";

type VerticalScrollableContentWithArrowsProps = {
  children: React.ReactNode;
  className?: string;
  viewportClassName?: string;
  arrowWrapperClassName?: string;
};

export function VerticalScrollableContentWithArrows({
  children,
  className,
  viewportClassName,
  arrowWrapperClassName,
}: VerticalScrollableContentWithArrowsProps) {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = React.useState(false);
  const [canScrollUp, setCanScrollUp] = React.useState(false);

  const checkScroll = React.useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;

    setCanScrollUp(el.scrollTop > 4);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  React.useEffect(() => {
    checkScroll();

    const el = viewportRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkScroll);

    // Keep arrow visibility in sync with dynamic content/resize changes.
    const resizeObserver = new ResizeObserver(() => checkScroll());
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll, children]);

  return (
    <div className={cn("relative h-full min-h-0", className)}>
      <div
        ref={viewportRef}
        onScroll={checkScroll}
        className={cn("h-full overflow-y-auto", viewportClassName)}
      >
        {children}
      </div>

      {canScrollUp && (
        <div className="absolute top-3 left-0 right-0 flex justify-center pointer-events-none">
          <div
            className={cn(
              "bg-white/95 rounded-full p-1 shadow-sm border border-gray-200",
              arrowWrapperClassName,
            )}
          >
            <ChevronUp className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      )}

      {canScrollDown && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center pointer-events-none">
          <div
            className={cn(
              "bg-white/95 rounded-full p-1 shadow-sm border border-gray-200",
              arrowWrapperClassName,
            )}
          >
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      )}
    </div>
  );
}
